import type { Json } from "../database.types";

const canonicalise = (value: Json, seen: Set<object>): string => {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Canonical JSON cannot contain a non-finite number.");
    return JSON.stringify(Object.is(value, -0) ? 0 : value);
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) throw new Error("Canonical JSON cannot contain a cycle.");
    seen.add(value);
    const result = `[${value.map((item) => canonicalise(item, seen)).join(",")}]`;
    seen.delete(value);
    return result;
  }
  if (typeof value === "object") {
    if (seen.has(value)) throw new Error("Canonical JSON cannot contain a cycle.");
    seen.add(value);
    const result = `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalise(value[key] as Json, seen)}`)
      .join(",")}}`;
    seen.delete(value);
    return result;
  }
  throw new Error("Canonical JSON contains an unsupported value.");
};

export const canonicalJsonStringify = (value: Json): string => canonicalise(value, new Set());

export async function canonicalJsonSha256(value: Json): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJsonStringify(value));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function bytesSha256(value: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", value as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export const isSha256 = (value: string) => /^[a-f0-9]{64}$/u.test(value);
